import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { basicHeaders } from '../utils';
import { PaginationParams } from '../types/common';
import { merge } from 'lodash';
import { GET_ADMIN_ISSUES } from '../../legacy/providers/graphql-queries/AdminIssues';
import {
  GET_ALL_DEVICES_LAST_UPDATE,
  GET_ANALYSIS_JOB_LIST,
  GET_CLIENTS_SUMMARY,
  GET_CUSTOMER_DATA,
  GET_DEVICE_LAST_UPDATE_LIST,
  GET_FLEET_AGGREGATIONS_BY_JOB_BY_TIME_PERIOD,
  GET_FLEET_ANALYSIS,
  GET_FLEET_DETAIL,
  GET_NEW_FLEET_DATA,
  GET_SUMMARY_DATA,
  GetFleetDetailVars,
  GetNewFleetDataVars,
  GetSummaryDataVars,
} from '../../legacy/providers/graphql-queries';
import { GET_VANADIUM_LOCATION } from '../../legacy/providers/graphql-queries/VanadiumLocation';
import { defaultGQLSearchVariables, makeSearchObject } from '../../utils/utils';
import { clientAssociatedTags, fullClientTags } from '../projects/clientsApi';
import { GatewayStatusEntry, GatewayStatusResolvers } from '../../@types/types.generated';

const globalAppId = import.meta.env.VITE_APP_APP_ID || 'projects-development';

type Alert = {
  job_id: string;
  alert_setting_id: string;
  alert_level: 'alert' | 'warn';
  date: string;
  count_of_days: number;
  power_logger_name: string;
  fleet_id: string;
  client_id: string;
  project_id: string;
  project_workflow_id: string;
};
type GetLatestAlertsResponse = {
  latestAlerts: Alert[];
};

export type BatteryMonitorStatus = 'healthy' | 'warning' | 'impaired' | 'offline';

export interface BatteryMonitorIssue {
  id: string;
  type: string;
}

export interface BatteryMonitorStatusEntry {
  batterySerial: string;
  status: BatteryMonitorStatus;
  issues: BatteryMonitorIssue[];
  lastReading: string | null;
}

type BatteryMonitorStatusesResponse = {
  batteryMonitorStatuses?: BatteryMonitorStatusEntry[];
};
export interface BatteryLifetime {
  batterySerial: string;
  status: string;
  calculatedThrough: string;
  observedDays: number;
  cycleLife: number | null;
  cyclesConsumed: number | null;
  cyclesRemaining: number | null;
  remainingPercent: number | null;
  projectedDaysRemaining: number | null;
}

export const fleetAnalyticsApi = createApi({
  reducerPath: 'fleetAnalyticsApi',
  tagTypes: [...fullClientTags],
  baseQuery: graphqlRequestBaseQuery({
    url: `${import.meta.env.VITE_APP_FLEET_ANALYSIS_SERVICE}/graphql`,
    prepareHeaders: basicHeaders,
  }),
  endpoints: (builder) => ({
    getBatteryLifetime: builder.query<BatteryLifetime[], { appId: string; fleetJobId: string }>({
      query: (variables) => ({
        document: gql`
          query BatteryLifetime($appId: String!, $fleetJobId: String!) {
            batteryLifetime(appId: $appId, fleetJobId: $fleetJobId) {
              batterySerial status calculatedThrough observedDays cycleLife
              cyclesConsumed cyclesRemaining remainingPercent projectedDaysRemaining
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: { batteryLifetime: BatteryLifetime[] }) => response.batteryLifetime,
      keepUnusedDataFor: 60,
    }),
    getLatestAlerts: builder.query<GetLatestAlertsResponse, void>({
      query: () => ({
        document: gql`
          query {
            latestAlerts {
              job_id
              alert_setting_id
              alert_level
              date
              count_of_days
              power_logger_name
              fleet_id
              client_id
              project_id
              project_workflow_id
            }
          }
        `,
      }),
    }),
    getMonitorProjects: builder.query<any, PaginationParams>({
      query: ({ pagination, search, sort }) => {
        const searchSort = merge({}, search, sort);
        const fullSearch = {};
        Object.keys(searchSort).forEach((key) => {
          fullSearch[key] = {
            value: search[key]?.value ?? '',
            operator: search[key]?.operator,
            sort: !sort[key] ? undefined : sort[key],
          };
        });
        return {
          variables: {
            page: pagination?.page || 1,
            perPage: pagination.perPage || 10,
            search: fullSearch,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          document: gql`
            query getMonitorProjects(
              $page: Int
              $perPage: Int
              $search: MonitorProjectsSearchInput
            ) {
              monitorProjects(pagination: { page: $page, perPage: $perPage }, search: $search) {
                meta {
                  currentPage
                  perPage
                  totalEntries
                  totalPages
                }
                results {
                  id
                  project_id
                  name
                  client_id
                  client_name
                  facility_id
                  facility_name
                  workflow_type
                  date_last_activity
                  date_created_at
                  fleet_id
                  job_id
                  unexpected_assets
                  alerts {
                    power_logger_name
                    alert_level
                  }
                  batteries {
                    manufacturer_id
                    status
                    chemistry
                    asset_type
                    firmware
                    is_licensed
                  }
                  gateways {
                    manufacturer_id
                    online_status
                    last_update
                  }
                }
              }
            }
          `,
        };
      },
      transformResponse: (response: any) => response?.monitorProjects,
    }),
    getClientsSummary: builder.query<any, PaginationParams>({
      query: (params) => {
        return {
          variables: defaultGQLSearchVariables(params),
          document: GET_CLIENTS_SUMMARY,
        };
      },
      providesTags: fullClientTags,
      transformResponse: (response: any) => response?.clientsSummary,
    }),

    getMeasureModelProjects: builder.query<any, PaginationParams>({
      query: ({ pagination, search, sort }) => {
        const searchSort = merge({}, search, sort);
        const fullSearch = {};
        Object.keys(searchSort).forEach((key) => {
          fullSearch[key] = {
            value: search[key]?.value ?? '',
            operator: search[key]?.operator,
            sort: !sort[key] ? undefined : sort[key],
          };
        });
        return {
          variables: {
            page: pagination?.page || 1,
            perPage: pagination.perPage || 10,
            search: fullSearch,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          document: gql`
            query getMeasureModelProjects(
              $page: Int
              $perPage: Int
              $search: MeasureModelProjectsSearchInput
            ) {
              measureModelProjects(
                pagination: { page: $page, perPage: $perPage }
                search: $search
              ) {
                meta {
                  currentPage
                  perPage
                  totalEntries
                  totalPages
                }
                results {
                  id
                  name
                  client_id
                  client_name
                  facility_id
                  facility_name
                  last_activity
                  workflow_type
                  created_at
                  owner_name
                  status
                  measure_status
                  measure_updated_at
                  measure_created_at
                  measure_batteries
                  measure_date_range
                  model_status
                  model_updated_at
                  model_created_at
                }
              }
            }
          `,
        };
      },
      transformResponse: (response: any) => response?.measureModelProjects,
    }),
    getFleetData: builder.query<any, void>({
      query: () => ({
        document: gql`
          query  {
            allFleets(appId: ${import.meta.env.VITE_APP_APP_ID || 'projects-development'}) {
              currentYear
              currentWeek
              fleets {
                clientId
                projectId
                workflowId
                fleetId
                appId
                jobId
                batteryCount
                vehicleCount
                warningsCurrentWeek
                criticalsCurrentWeek
              }
            }
          }
        `,
      }),
    }),

    getAdminIssues: builder.query<any, { fleetId: number }>({
      query: ({ fleetId }) => ({
        document: GET_ADMIN_ISSUES,
        variables: {
          fleetId: fleetId,
        },
      }),
    }),
    getNewFleetData: builder.query<number, GetNewFleetDataVars>({
      query: ({ clientId, projectId, fleetId, workflowId, homeAccountId }) => ({
        document: GET_NEW_FLEET_DATA,
        variables: {
          clientId,
          projectId,
          fleetId,
          workflowId,
          homeAccountId,
        },
      }),
    }),
    getSummaryData: builder.query<any, GetSummaryDataVars>({
      query: ({ appId = globalAppId, fleetJobId, jobIds, startDate, endDate }) => ({
        document: GET_SUMMARY_DATA,
        variables: {
          appId,
          fleetJobId,
          jobIds,
          startDate,
          endDate,
        },
      }),
    }),
    getFleetDetail: builder.query<any, GetFleetDetailVars>({
      query: ({ deviceId, assetId, dates, timeZoneOffset }) => ({
        document: GET_FLEET_DETAIL,
        variables: { deviceId, assetId, dates, timeZoneOffset },
      }),
    }),
    getVanadiumLocationData: builder.query<any, { fleetId: number }>({
      query: ({ fleetId }) => ({
        document: GET_VANADIUM_LOCATION,
        variables: { fleetId },
      }),
    }),
    // TODO: Rename to getBatteryLocationData to be chemistry-agnostic
    // The vanadiumLocation query should return all battery types for the fleet
    getBatteryLocationData: builder.query<any, { fleetId: number }>({
      query: ({ fleetId }) => ({
        document: GET_VANADIUM_LOCATION, // Reusing same query for now
        variables: { fleetId },
      }),
    }),
    getAnalysisJobList: builder.query<any, { appId: number; jobId: string }>({
      query: ({ appId, jobId }) => ({
        document: GET_ANALYSIS_JOB_LIST,
        variables: { appId, jobId },
      }),
    }),
    getCustomerData: builder.query<any, { appId: string }>({
      query: ({ appId }) => ({
        document: GET_CUSTOMER_DATA,
        variables: { appId },
      }),
    }),
    getFleetAggregationsByJobByTimePeriod: builder.query<
      any,
      { appId: string; jobId: string; type: 'alarms' | 'summary'; period: string }
    >({
      query: ({ appId, jobId, type, period }) => ({
        document: GET_FLEET_AGGREGATIONS_BY_JOB_BY_TIME_PERIOD,
        variables: { appId, jobId, type, period },
      }),
      transformResponse: (response: any) => response?.fleetAggregationByJobTimePeriod,
    }),
    // This query is used to fetch fleet analysis data for a specific workflow
    // It requires clientId, projectId, and workflowId as parameters
    // prettier-ignore
    getFleetAnalysis: builder.query<any,{ clientId: number; projectId: number; workflowId: number }>({
      query: ({ clientId, projectId, workflowId }) => ({
        document: GET_FLEET_ANALYSIS,
        variables: { clientId, projectId, workflowId },
      }),
    }),
    getFleetSummaryByJobIds: builder.query<any, { appId: string; jobId: string[]; period: string }>(
      {
        query: ({ appId, jobIds, period }) => ({
          document: gql`
            query getFleetSummaryByJobIds(
              $appId: String!
              $jobIds: [String!]!
              $period: FleetAggregationPeriod!
            ) {
              fleetSummaryByJobIds(app_id: $appId, job_ids: $jobIds, period: $period) {
                power_logger_name
                job_id
                date
              }
            }
          `,
          variables: { appId, jobIds, period },
        }),
        transformResponse: (response: any) => response?.fleetSummaryByJobIds,
      }
    ),
    getGatewayStatusList: builder.query<GatewayStatusEntry[], string[]>({
      query: (ids) => ({
        document: gql`
          query getGatewayStatusList($ids: [String!]!) {
            gatewayStatusList(gatewaySerials: $ids) {
              gatewaySerial
              createdAt
              updatedAt
              status {
                status
                eir_count
                warnings
                errors
                version
                factory_seq
                user_seq
                uptime_sec
                internal_temp_f
                humidity
              }
            }
          }
        `,
        variables: { ids },
      }),
      transformResponse: (response) => response?.gatewayStatusList || [],
    }),
    getDeviceLastUpdateList: builder.query<any, { deviceIds: string[] }>({
      query: ({ deviceIds }) => ({
        document: GET_DEVICE_LAST_UPDATE_LIST,
        variables: { deviceIds },
      }),
      transformResponse: (response: any) => response?.deviceLastUpdateList || [],
    }),
    getAllDevicesLastUpdate: builder.query<any, void>({
      query: () => ({
        document: GET_ALL_DEVICES_LAST_UPDATE,
      }),
      transformResponse: (response: any) => response?.allDevicesLastUpdate || [],
    }),
    getBatteryMonitorStatuses: builder.query<BatteryMonitorStatusEntry[], number>({
      query: (fleetId) => ({
        document: gql`
          query getBatteryMonitorStatuses($fleetId: Int!) {
            batteryMonitorStatuses(fleetId: $fleetId) {
              batterySerial
              status
              lastReading
              issues {
                id
                type
              }
            }
          }
        `,
        variables: { fleetId },
      }),
      transformResponse: (response: BatteryMonitorStatusesResponse) =>
        response?.batteryMonitorStatuses || [],
    }),
  }),
});

export const {
  useGetLatestAlertsQuery,
  useLazyGetMonitorProjectsQuery,
  useLazyGetMeasureModelProjectsQuery,
  useLazyGetAdminIssuesQuery,
  useGetNewFleetDataQuery,
  useLazyGetFleetDataQuery,
  useLazyGetNewFleetDataQuery,
  useLazyGetSummaryDataQuery,
  useLazyGetFleetDetailQuery,
  useLazyGetVanadiumLocationDataQuery,
  useLazyGetBatteryLocationDataQuery, // New unified battery location query
  useGetAnalysisJobListQuery,
  useLazyGetAnalysisJobListQuery,
  useLazyGetFleetAnalysisQuery,
  useLazyGetClientsSummaryQuery,
  useGetGatewayStatusListQuery,
  useGetFleetSummaryByJobIdsQuery,
  useGetCustomerDataQuery,
  useLazyGetCustomerDataQuery,
  useGetDeviceLastUpdateListQuery,
  useGetFleetAggregationsByJobByTimePeriodQuery,
  useGetAllDevicesLastUpdateQuery,
  useGetBatteryLifetimeQuery,
  useGetBatteryMonitorStatusesQuery,
} = fleetAnalyticsApi;
